/**
 * Travel Carbon Estimator
 * Copyright (C) 2026  Manuel Leduc
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
import { i18n } from '@/lib/i18n'
import type { Ref } from 'vue'

export interface LocaleEntry {
  code: string
  label: string
  rtl: boolean
}

export const availableLocales: LocaleEntry[] = [
  { code: 'en', label: 'English', rtl: false },
  { code: 'fr', label: 'Français', rtl: false },
]

const LOCALE_STORAGE_KEY = 'tce-locale'

export function detectLocale(): string {
  const codes = availableLocales.map((l) => l.code)
  for (const lang of navigator.languages ?? []) {
    // Exact match
    if (codes.includes(lang)) return lang
    // Base-language match (e.g. 'fr-CA' → 'fr')
    const base = lang.split('-')[0]
    if (base && codes.includes(base)) return base
  }
  return 'en'
}

export async function setLocale(code: string): Promise<void> {
  // Fall back to 'en' for unrecognized codes (stale localStorage, missing locale file)
  if (!availableLocales.some((l) => l.code === code)) {
    code = 'en'
  }

  // Lazy-load locale JSON if not already loaded
  if (!i18n.global.availableLocales.includes(code as never)) {
    const messages = await import(`../locales/${code}.json`)
    i18n.global.setLocaleMessage(code, messages.default)
  }

  const localRef = i18n.global.locale as Ref<string>
  localRef.value = code

  const entry = availableLocales.find((l) => l.code === code)
  document.documentElement.setAttribute('dir', entry?.rtl ? 'rtl' : 'ltr')

  localStorage.setItem(LOCALE_STORAGE_KEY, code)
}

export function getSavedLocale(): string | null {
  return localStorage.getItem(LOCALE_STORAGE_KEY)
}
